import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, isRef as _isRef, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "filter-bar" };
const _hoisted_2 = { class: "filter-row-secondary" };
const _hoisted_3 = {
    key: 0,
    class: "loading-state"
};
const _hoisted_4 = {
    key: 0,
    class: "drill-crumb"
};
const _hoisted_5 = { class: "crumb-current" };
const _hoisted_6 = { class: "layout-toolbar" };
const _hoisted_7 = {
    class: "card-drag-handle",
    "aria-label": "Drag to reorder"
};
const _hoisted_8 = { class: "chart-title" };
const _hoisted_9 = { class: "dash-scroll" };
const _hoisted_10 = { class: "dash-scroll" };
const _hoisted_11 = { class: "dash-scroll" };
const _hoisted_12 = { class: "dash-scroll" };
import { ref, computed, watch, onMounted, nextTick, getCurrentInstance, inject, useTemplateRef } from 'vue';
import { GridLayout, GridItem } from 'grid-layout-plus';
import { useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking } from '@/composables/useBanking';
import { useContent } from '@/composables/useContent';
import { useTransactions } from '@/composables/useTransactions';
import { getSpendingAnalysis, getTransactionClassificationTree, buildSpendingOverviewBreakdown, buildCategoryRows, buildTransactionGroups, groupTransactionsByDay, formatTimestampString, findCurrentMonthIndex, getMonthRangeForSelection, getDefaultMonthRange, SPENDING_BREAKDOWN_PALETTE, } from '@/composables/useSpendingAnalysis';
import { selectedFilter, selectedPeriod, selectedAccount, selectedSubCategory, selectedSubCategoryColour, selectedMerchant, dateSelection, snapToSpendingCycle, } from '@/components/elements/spendingAnalysisFilterState';
import { useSpendingPlansSidebar } from '@/composables/useSpendingPlansSidebar';
import { pendingWriteRevalidate, consumePendingWriteRevalidate, inflightCategoryUpdate } from '@/components/elements/transactionUpdateState';
import SpendingFilterTabs from '@/components/elements/SpendingFilterTabs.vue';
import PeriodSelector from '@/components/elements/PeriodSelector.vue';
import SpendingSummaryCard from '@/components/elements/SpendingSummaryCard.vue';
import CategoryAnalysisList from '@/components/elements/CategoryAnalysisList.vue';
import DesktopUncategorisedBulk from '../components/DesktopUncategorisedBulk.vue';
import RecurringPaymentsPage from '@/components/RecurringPaymentsPage.vue';
import SpendByCustomerTagPage from '@/components/SpendByCustomerTagPage.vue';
import TransactionsList from '@/components/elements/TransactionsList.vue';
import { currentTaxYearLabel, formatMonthYearShort, formatResponseRange } from '@/utils/helpers';
import SpendingTreemapPage from '@/components/SpendingTreemapPage.vue';
import TopMerchantsChart from '../components/TopMerchantsChart.vue';
import SettingsOverlay from '../components/SettingsOverlay.vue';
import AccountSelectorDesktop from '../components/AccountSelectorDesktop.vue';
import DateRangeDialog from '../components/DateRangeDialog.vue';
import ChangeCategoryDialog from '../components/ChangeCategoryDialog.vue';
import TransactionViewDialog from '../components/TransactionViewDialog.vue';
// ---------- Constants ----------
const LAYOUT_KEY = 'sa_dash_layout_v1';
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendingAnalysisPage',
    emits: ["colour-mode-changed"],
    setup(__props, { emit: __emit }) {
        const PERIODS = [
            { key: 'month', label: 'Month' },
            { key: 'Year', label: 'Year' },
            { key: 'tax', label: currentTaxYearLabel() },
        ];
        // ---------- Composables ----------
        const state = useAppState();
        const router = useRouter();
        const instance = getCurrentInstance();
        const cookies = instance?.appContext.config.globalProperties.$cookies;
        const { getCustomer, savePreferences } = useCustomer();
        const { getBankingInfo, getRecurringPayments } = useBanking();
        const { getPlans } = useContent();
        const transactionsApi = useTransactions();
        const registerSidebarFn = inject('registerSidebar', null);
        const { fetchAndRegisterSpendingPlans, registerSpendingPlansSidebar } = useSpendingPlansSidebar(registerSidebarFn);
        const singleTxnDialog = useTemplateRef('singleTxnDialog');
        const txnViewDialog = useTemplateRef('txnViewDialog');
        const selectedTxn = ref(null);
        // Drives the per-row spinner while a category change is in flight (cleared when
        // the SWR poll confirms the new classification has landed).
        const updatingTransactionId = ref(null);
        const emit = __emit;
        // ---------- Local UI state ----------
        const colourMode = ref('light');
        const ready = ref(false);
        const showSettings = ref(false);
        const fetchReady = ref(false);
        const summaryLoading = ref(false);
        const summary = ref(null);
        const filterTabs = ref([{ key: 'all', label: 'All' }]);
        const periods = ref(PERIODS);
        const recurringPayments = ref(null);
        const dateDialogOpen = ref(false);
        const customer = computed(() => state.customer);
        const formatter = computed(() => state.formatter);
        const safeTop = computed(() => parseInt(cookies?.get('st') ?? 0));
        // ---------- Filter / view computeds ----------
        const selectedAccountIds = computed(() => {
            const key = selectedAccount.value;
            const accounts = (state.bankInfo?.accounts || []).filter((a) => a.source === 0);
            if (!accounts.length || key === 'all')
                return undefined;
            const typeMap = { current: 'Current', credit: 'Credit', savings: 'Savings' };
            if (typeMap[key]) {
                const ids = accounts.filter((a) => a.accountType?.prettyName === typeMap[key]).map((a) => a.id);
                return ids.length ? ids : undefined;
            }
            const match = accounts.find((a) => a.id === key);
            return match ? [match.id] : undefined;
        });
        const isAllView = computed(() => selectedFilter.value === 'all' && !selectedSubCategory.value && !selectedMerchant.value);
        const selectedTab = computed(() => filterTabs.value.find(t => t.key === selectedFilter.value));
        const selectedCategoryName = computed(() => (selectedTab.value && selectedTab.value.key !== 'all') ? selectedTab.value.label : undefined);
        const selectedFilterLabel = computed(() => selectedMerchant.value || selectedSubCategory.value || selectedTab.value?.label || '');
        const selectedFilterColour = computed(() => {
            if (selectedSubCategory.value) {
                return selectedSubCategoryColour.value || SPENDING_BREAKDOWN_PALETTE[0];
            }
            if (selectedTab.value?.color)
                return selectedTab.value.color;
            const parent = (summary.value?.parentCategories ?? []).find((p) => p.name === selectedFilterLabel.value);
            const txColour = parent?.categoryColor ?? parent?.transactions?.[0]?.categoryColor ?? parent?.color ?? parent?.transactions?.[0]?.color;
            return txColour ?? SPENDING_BREAKDOWN_PALETTE[0];
        });
        // ---------- Range / fetch ----------
        const monthRange = computed(() => getMonthRangeForSelection(dateSelection.value, selectedPeriod.value));
        const analysisRange = computed(() => {
            if (monthRange.value) {
                const [fy, fm] = monthRange.value.startMonth.split('-').map(Number);
                const [ty, tm] = monthRange.value.endMonth.split('-').map(Number);
                return {
                    fromDate: new Date(Date.UTC(fy, fm - 1, 1)),
                    toDate: new Date(Date.UTC(ty, tm, 1)),
                };
            }
            return getDefaultMonthRange(snapToSpendingCycle.value);
        });
        function fetchSummary() {
            const { fromDate, toDate } = analysisRange.value;
            const opts = {
                accountIds: selectedAccountIds.value,
                snapToSpendingCycle: snapToSpendingCycle.value,
            };
            if (selectedMerchant.value)
                opts.merchant = selectedMerchant.value;
            else if (selectedSubCategory.value)
                opts.subCategory = selectedSubCategory.value;
            else if (selectedCategoryName.value)
                opts.category = selectedCategoryName.value;
            // Serve the cached payload instantly, then revalidate in the background (SWR).
            // After a write, poll until the server payload reflects the change.
            const pollUntilChanged = consumePendingWriteRevalidate() || !!inflightCategoryUpdate.value;
            summaryLoading.value = true;
            getSpendingAnalysis(fromDate, toDate, opts, (result, isStale) => {
                if (result)
                    summary.value = result;
                if (!(pollUntilChanged && isStale))
                    summaryLoading.value = false;
                // The fresh post-write payload has landed — drop the row spinner.
                if (!isStale && pollUntilChanged)
                    updatingTransactionId.value = null;
            }, /* refreshCache */ false, /* staleWhileRevalidate */ true, pollUntilChanged);
        }
        // ---------- Data shaping ----------
        const hasSummary = computed(() => !!summary.value && Array.isArray(summary.value.parentCategories) && summary.value.parentCategories.length > 0);
        const rangeLabel = computed(() => {
            const r = summary.value;
            if (r?.fromDate && r?.toDate)
                return formatResponseRange(r.fromDate, r.toDate);
            const sel = dateSelection.value;
            if (sel) {
                if (sel.type === 'months' && sel.startMonth) {
                    const start = formatMonthYearShort(sel.startMonth);
                    if (!sel.endMonth || sel.endMonth === sel.startMonth)
                        return start;
                    return `${start} - ${formatMonthYearShort(sel.endMonth)}`;
                }
                return sel.label ?? '';
            }
            if (selectedPeriod.value === 'month')
                return 'This month';
            if (selectedPeriod.value === 'Year')
                return 'This year';
            if (selectedPeriod.value === 'tax')
                return 'This tax year';
            return '';
        });
        const spendingSummary = computed(() => {
            const r = summary.value;
            if (!r || !isAllView.value) {
                return { rangeLabel: rangeLabel.value, net: 0, totalIn: 0, totalOut: 0, breakdown: [] };
            }
            const breakdown = buildSpendingOverviewBreakdown(r.parentCategories, (name) => filterTabs.value.find(t => t.label === name)?.color ?? null);
            return {
                rangeLabel: rangeLabel.value,
                net: r.net ?? ((r.totalIn ?? 0) - (r.totalOut ?? 0)),
                totalIn: r.totalIn ?? 0,
                totalOut: r.totalOut ?? 0,
                breakdown,
            };
        });
        const categorySliceBars = computed(() => {
            const slices = summary.value?.timeSlicedBreakdowns;
            if (!Array.isArray(slices))
                return [];
            const now = Date.now();
            return slices.map((s) => {
                const from = s.fromDate ? new Date(s.fromDate).getTime() : 0;
                return {
                    label: s.label,
                    value: Math.abs(s.totalOut ?? 0) || Math.abs(s.net ?? 0),
                    isFuture: from > now,
                    fromDate: s.fromDate,
                    toDate: s.toDate,
                };
            });
        });
        const dataIsWeekly = computed(() => {
            const slices = summary.value?.timeSlicedBreakdowns;
            return Array.isArray(slices) && slices.length > 0 && slices.every((s) => s.week === true);
        });
        const currentMonthIndex = computed(() => findCurrentMonthIndex(categorySliceBars.value));
        const currentCategories = computed(() => buildCategoryRows(summary.value, isAllView.value, selectedSubCategory.value, selectedSubCategoryColour.value));
        const currentMerchants = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            const byName = new Map();
            for (const p of parents) {
                const merchants = Array.isArray(p.spendByMerchant) ? p.spendByMerchant : [];
                for (const m of merchants) {
                    const totalIn = m.totalIn ?? 0;
                    const totalOut = m.totalOut ?? 0;
                    const amount = Math.abs(totalOut > totalIn ? totalOut : totalIn);
                    if (amount <= 0)
                        continue;
                    const wellKnown = Array.isArray(m.transactions) && m.transactions.some((t) => t.wellKnownMerchant);
                    const iconPath = m.transactions?.[0]?.icon ?? p.transactions?.[0]?.icon;
                    const iconColour = m.transactions?.[0]?.color ?? p.transactions?.[0]?.color ?? p.color;
                    const existing = byName.get(m.merchantName);
                    if (existing) {
                        existing.amount += amount;
                        existing.transactionCount += m.transactionCount ?? 0;
                        if (wellKnown)
                            existing.merchant = true;
                        if (!existing.iconPath && iconPath)
                            existing.iconPath = iconPath;
                        if (!existing.iconColour && iconColour)
                            existing.iconColour = iconColour;
                    }
                    else {
                        byName.set(m.merchantName, {
                            name: m.merchantName,
                            transactionCount: m.transactionCount ?? 0,
                            amount,
                            merchant: wellKnown ? true : undefined,
                            iconPath,
                            iconColour,
                        });
                    }
                }
            }
            return [...byName.values()].sort((a, b) => b.amount - a.amount);
        });
        const apiTransactionGroups = computed(() => buildTransactionGroups(summary.value));
        const topMerchants = computed(() => currentMerchants.value.slice(0, 8).map(m => ({ name: m.name, value: m.amount })));
        // Net total shown in the category-mode summary header (date range + amount).
        const categoryHeaderAmount = computed(() => {
            if (summaryLoading.value)
                return undefined;
            const r = summary.value;
            if (!r)
                return undefined;
            return (r.totalIn ?? 0) - (r.totalOut ?? 0);
        });
        function isUncategorised(t) {
            return !t.subClassification || t.subClassification === 'Uncategorised' || t.subClassification === 'Uncategorized';
        }
        const uncategorisedTransactionGroups = computed(() => {
            const parents = Array.isArray(summary.value?.parentCategories) ? summary.value.parentCategories : [];
            const flat = [];
            const seen = new Set();
            for (const p of parents) {
                const txs = Array.isArray(p.transactions) ? p.transactions : [];
                for (const t of txs) {
                    if (!isUncategorised(t))
                        continue;
                    const id = t.id ?? `${t.merchantName}-${t.timestamp}-${t.amount}`;
                    if (seen.has(id))
                        continue;
                    seen.add(id);
                    flat.push({
                        id,
                        merchantName: t.merchantName,
                        amount: t.amount,
                        timestamp: t.timestamp,
                        timestampString: formatTimestampString(t.timestamp),
                        wellKnownMerchant: t.wellKnownMerchant,
                        effectiveClassification: {
                            mgClassification: t.classification,
                            mgSubClassification: t.subClassification,
                            color: p.color ?? t.color,
                            iconPath: t.icon,
                        },
                    });
                }
            }
            return groupTransactionsByDay(flat);
        });
        const uncategorisedCount = computed(() => uncategorisedTransactionGroups.value.reduce((sum, g) => sum + g.length, 0));
        // ---------- Dashboard layout (grid-layout-plus: draggable + resizable tiles) ----------
        const ALL_CARDS = ['insight', 'treemap', 'categories', 'transactions', 'recurring', 'merchants', 'tags'];
        const DEFAULT_LAYOUT = {
            insight: { x: 0, y: 0, w: 6, h: 19 },
            treemap: { x: 6, y: 0, w: 6, h: 19 },
            categories: { x: 0, y: 19, w: 6, h: 17 },
            transactions: { x: 6, y: 19, w: 6, h: 17 },
            recurring: { x: 0, y: 36, w: 6, h: 17 },
            merchants: { x: 6, y: 36, w: 6, h: 17 },
            tags: { x: 0, y: 53, w: 6, h: 17 },
        };
        function defaultPositions() {
            const o = {};
            for (const id of ALL_CARDS)
                o[id] = { ...DEFAULT_LAYOUT[id] };
            return o;
        }
        function loadPositions() {
            const pos = defaultPositions();
            try {
                const saved = JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null');
                if (saved && typeof saved === 'object') {
                    for (const id of ALL_CARDS)
                        if (saved[id])
                            pos[id] = { ...pos[id], ...saved[id] };
                }
            }
            catch { /* ignore malformed */ }
            return pos;
        }
        const positions = ref(loadPositions());
        const layout = ref([]);
        const availableCards = computed(() => {
            const ids = [];
            if (hasSummary.value)
                ids.push('insight', 'treemap');
            // Categories, transactions and top merchants always render (empty when there's
            // no analysis yet) — only hidden while drilled into a single merchant.
            if (!selectedMerchant.value)
                ids.push('categories');
            ids.push('transactions');
            if (recurringPayments.value && recurringPayments.value.payments.length > 0)
                ids.push('recurring');
            if (!selectedMerchant.value)
                ids.push('merchants');
            // Customer-tag spend is self-contained (loads its own data) — show it except
            // while drilled into a single merchant.
            if (!selectedMerchant.value)
                ids.push('tags');
            return ids;
        });
        function rebuildLayout() {
            layout.value = availableCards.value.map(id => ({ i: id, ...positions.value[id] }));
        }
        // Persist positions whenever grid-layout-plus moves/resizes/compacts a tile.
        function onLayoutUpdated(updated) {
            for (const it of updated)
                positions.value[it.i] = { x: it.x, y: it.y, w: it.w, h: it.h };
            localStorage.setItem(LAYOUT_KEY, JSON.stringify(positions.value));
        }
        // Per-card extra classes (dark-mode / chart-fill overrides rely on these).
        function cardClass(id) {
            if (id === 'treemap')
                return 'dash-treemap';
            if (id === 'recurring')
                return 'recurring-card';
            if (id === 'merchants')
                return 'dash-stretch';
            if (id === 'tags')
                return 'tags-card';
            return '';
        }
        function resetLayout() {
            positions.value = defaultPositions();
            localStorage.removeItem(LAYOUT_KEY);
            rebuildLayout();
        }
        // Rebuild the visible tile list when the set of available cards changes.
        watch(() => availableCards.value.join(), rebuildLayout, { immediate: true });
        // ---------- Event handlers ----------
        // Date range (ISO) passed to the embedded bulk page — prefer the server-resolved
        // window, fall back to the local analysis range.
        const bulkRange = computed(() => {
            const from = summary.value?.fromDate ?? analysisRange.value.fromDate;
            const to = summary.value?.toDate ?? analysisRange.value.toDate;
            return {
                from: typeof from === 'string' ? from : from?.toISOString(),
                to: typeof to === 'string' ? to : to?.toISOString(),
            };
        });
        function onBulkCategorised() {
            // New categorisations have landed — SWR+poll until the summary reflects them.
            pendingWriteRevalidate.value = true;
            fetchSummary();
        }
        function onDateRangeSelect(selection) {
            dateSelection.value = selection;
        }
        function onClearDateSelection() {
            dateSelection.value = null;
            selectedPeriod.value = 'month';
        }
        function onMonthBarClick(bar) {
            if (!bar.fromDate)
                return;
            const d = new Date(bar.fromDate);
            if (Number.isNaN(d.getTime()))
                return;
            const y = d.getUTCFullYear();
            const m = String(d.getUTCMonth() + 1).padStart(2, '0');
            const monthKey = `${y}-${m}`;
            dateSelection.value = {
                type: 'months',
                startMonth: monthKey,
                endMonth: monthKey,
                label: formatMonthYearShort(monthKey),
            };
            selectedPeriod.value = 'month';
        }
        function onCategoryClick(name) {
            const tab = filterTabs.value.find(t => t.label === name);
            if (tab)
                selectedFilter.value = tab.key;
        }
        function onSubCategoryClick(name, colour) {
            selectedSubCategory.value = name;
            selectedSubCategoryColour.value = colour || '';
        }
        function onMerchantClick(name) {
            selectedMerchant.value = name;
        }
        function goToTransaction(t) {
            // Desktop: don't leak into the mobile TransactionDetailPage. Open a lightweight
            // transaction view dialog first; the user changes category from there.
            selectedTxn.value = t;
            nextTick(() => txnViewDialog.value?.openDrawer());
        }
        function onChangeCategoryFromView() {
            txnViewDialog.value?.closeDrawer();
            nextTick(() => singleTxnDialog.value?.openDrawer());
        }
        function onSingleCategoryApply(payload) {
            const t = selectedTxn.value;
            if (!payload.classification || !t?.id) {
                singleTxnDialog.value?.closeDrawer();
                return;
            }
            // Spinner on the row until the SWR poll confirms the change.
            updatingTransactionId.value = t.id;
            transactionsApi.applyCategoryToTransactions([t], payload, () => {
                // SWR+poll until the SA payload reflects the change. Don't clear the cache —
                // it's the baseline the poll compares against.
                pendingWriteRevalidate.value = true;
                singleTxnDialog.value?.closeDrawer();
                fetchSummary();
            });
        }
        function onBack() {
            if (selectedMerchant.value) {
                selectedMerchant.value = '';
            }
            else if (selectedSubCategory.value) {
                selectedSubCategory.value = '';
            }
            else if (selectedFilter.value !== 'all') {
                selectedFilter.value = 'all';
            }
            window.scrollTo({ top: 0 });
        }
        function selectColourMode(mode) {
            colourMode.value = mode;
            localStorage.setItem('mg_colour_mode', mode);
            emit('colour-mode-changed', mode);
        }
        function updatePref(key, value) {
            if (customer.value?.preferences) {
                customer.value.preferences[key] = value;
                savePreferences();
            }
        }
        // ---------- Lifecycle ----------
        onMounted(() => {
            const savedMode = localStorage.getItem('mg_colour_mode');
            colourMode.value = savedMode || 'light';
            getPlans();
            getRecurringPayments((res) => { recurringPayments.value = res; });
            getCustomer(() => {
                getBankingInfo(() => {
                    getTransactionClassificationTree((tree) => {
                        if (tree?.parents) {
                            const apiTabs = tree.parents.map((p) => ({
                                key: p.parent?.key ?? p.name,
                                label: p.name,
                                categoryId: p.parent?.id,
                                color: p.categoryColor ?? p.parent?.categoryColor ?? p.color ?? p.parent?.color,
                            }));
                            const order = ['Income', 'Essentials', 'Lifestyle', 'Future', 'General'];
                            apiTabs.sort((a, b) => {
                                const ai = order.indexOf(a.label);
                                const bi = order.indexOf(b.label);
                                return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
                            });
                            filterTabs.value = [{ key: 'all', label: 'All' }, ...apiTabs];
                        }
                        fetchReady.value = true;
                        ready.value = true;
                        fetchSummary();
                        registerSpendingPlansSidebar({
                            onOpenSettings: () => { showSettings.value = true; },
                            onManagePlans: () => { router.push('/desktop/spendingplan'); }
                        });
                        fetchAndRegisterSpendingPlans({
                            onOpenSettings: () => { showSettings.value = true; },
                            onManagePlans: () => { router.push('/desktop/spendingplan'); }
                        });
                    });
                });
            });
        });
        watch([analysisRange, selectedAccountIds, snapToSpendingCycle, selectedFilter, selectedSubCategory, selectedMerchant], () => {
            if (!fetchReady.value)
                return;
            fetchSummary();
        });
        watch([selectedFilter, selectedSubCategory, selectedMerchant], () => {
            window.scrollTo({ top: 0 });
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            const _component_v_icon = _resolveComponent("v-icon");
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["desktop-analysis-page", colourMode.value])
            }, [
                (showSettings.value)
                    ? (_openBlock(), _createBlock(SettingsOverlay, {
                        key: 0,
                        customer: customer.value,
                        colourMode: colourMode.value,
                        safeTop: safeTop.value,
                        onClose: _cache[0] || (_cache[0] = ($event) => (showSettings.value = false)),
                        onUpdatePref: updatePref,
                        onSelectColourMode: selectColourMode
                    }, null, 8, ["customer", "colourMode", "safeTop"]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createElementVNode("div", _hoisted_1, [
                            (!_unref(selectedSubCategory) && !_unref(selectedMerchant))
                                ? (_openBlock(), _createBlock(SpendingFilterTabs, {
                                    key: 0,
                                    modelValue: _unref(selectedFilter),
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (_isRef(selectedFilter) ? (selectedFilter).value = $event : null)),
                                    tabs: filterTabs.value
                                }, null, 8, ["modelValue", "tabs"]))
                                : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_2, [
                                _createVNode(PeriodSelector, {
                                    modelValue: _unref(selectedPeriod),
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (_isRef(selectedPeriod) ? (selectedPeriod).value = $event : null)),
                                    periods: periods.value,
                                    dateSelection: _unref(dateSelection),
                                    onOpenDrawer: _cache[3] || (_cache[3] = ($event) => (dateDialogOpen.value = true)),
                                    onClearSelection: onClearDateSelection
                                }, null, 8, ["modelValue", "periods", "dateSelection"])
                            ]),
                            _createVNode(AccountSelectorDesktop, {
                                modelValue: _unref(selectedAccount),
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => (_isRef(selectedAccount) ? (selectedAccount).value = $event : null))
                            }, null, 8, ["modelValue"])
                        ]),
                        (!ready.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                _createVNode(_component_v_skeleton_loader, {
                                    type: "card",
                                    height: "120px",
                                    class: "mb-4"
                                }),
                                _createVNode(_component_v_skeleton_loader, {
                                    type: "card",
                                    height: "300px",
                                    class: "mb-4"
                                })
                            ]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                (_unref(selectedSubCategory) || _unref(selectedMerchant))
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                        _createElementVNode("button", {
                                            class: "crumb-back",
                                            onClick: onBack
                                        }, [
                                            _createVNode(_component_v_icon, { size: "16" }, {
                                                default: _withCtx(() => _cache[7] || (_cache[7] = [
                                                    _createTextVNode("mdi-arrow-left")
                                                ])),
                                                _: 1
                                            }),
                                            _cache[8] || (_cache[8] = _createElementVNode("span", null, "Back", -1))
                                        ]),
                                        _createElementVNode("span", _hoisted_5, _toDisplayString(selectedFilterLabel.value), 1)
                                    ]))
                                    : _createCommentVNode("", true),
                                _createVNode(DesktopUncategorisedBulk, {
                                    count: uncategorisedCount.value,
                                    range: bulkRange.value,
                                    colourMode: colourMode.value,
                                    onCategorised: onBulkCategorised,
                                    onViewTransaction: goToTransaction
                                }, null, 8, ["count", "range", "colourMode"]),
                                _createElementVNode("div", _hoisted_6, [
                                    _createElementVNode("button", {
                                        class: "reset-layout-btn",
                                        onClick: resetLayout
                                    }, [
                                        _createVNode(_component_v_icon, { size: "16" }, {
                                            default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                _createTextVNode("mdi-restore")
                                            ])),
                                            _: 1
                                        }),
                                        _cache[10] || (_cache[10] = _createElementVNode("span", null, "Reset layout", -1))
                                    ])
                                ]),
                                _createVNode(_unref(GridLayout), {
                                    layout: layout.value,
                                    "onUpdate:layout": _cache[5] || (_cache[5] = ($event) => ((layout).value = $event)),
                                    "col-num": 12,
                                    "row-height": 10,
                                    margin: [20, 20],
                                    "is-draggable": true,
                                    "is-resizable": true,
                                    "vertical-compact": true,
                                    onLayoutUpdated: onLayoutUpdated
                                }, {
                                    default: _withCtx(() => [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(layout.value, (item) => {
                                            return (_openBlock(), _createBlock(_unref(GridItem), {
                                                key: item.i,
                                                x: item.x,
                                                y: item.y,
                                                w: item.w,
                                                h: item.h,
                                                i: item.i,
                                                "drag-allow-from": ".card-drag-handle",
                                                "drag-ignore-from": ""
                                            }, {
                                                default: _withCtx(() => [
                                                    _createElementVNode("div", {
                                                        class: _normalizeClass(["chart-card", [colourMode.value, cardClass(item.i)]])
                                                    }, [
                                                        _createElementVNode("div", _hoisted_7, [
                                                            _createVNode(_component_v_icon, { size: "18" }, {
                                                                default: _withCtx(() => _cache[11] || (_cache[11] = [
                                                                    _createTextVNode("mdi-drag")
                                                                ])),
                                                                _: 1
                                                            })
                                                        ]),
                                                        (item.i === 'insight')
                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                (isAllView.value)
                                                                    ? (_openBlock(), _createBlock(SpendingSummaryCard, {
                                                                        key: "overview",
                                                                        mode: "overview",
                                                                        data: spendingSummary.value,
                                                                        chartHeight: 240,
                                                                        lockExpanded: true,
                                                                        onCategoryClick: onCategoryClick
                                                                    }, null, 8, ["data"]))
                                                                    : (_openBlock(), _createBlock(SpendingSummaryCard, {
                                                                        key: 'category-' + (_unref(selectedMerchant) || _unref(selectedSubCategory) || _unref(selectedFilter)),
                                                                        mode: "category",
                                                                        categoryLabel: selectedFilterLabel.value,
                                                                        rangeLabel: rangeLabel.value,
                                                                        amount: categoryHeaderAmount.value,
                                                                        monthlyData: categorySliceBars.value,
                                                                        weeklyData: dataIsWeekly.value ? categorySliceBars.value : undefined,
                                                                        currentMonthIndex: currentMonthIndex.value,
                                                                        barColour: selectedFilterColour.value,
                                                                        chartHeight: 240,
                                                                        onMonthClick: onMonthBarClick
                                                                    }, null, 8, ["categoryLabel", "rangeLabel", "amount", "monthlyData", "weeklyData", "currentMonthIndex", "barColour"]))
                                                            ], 64))
                                                            : (item.i === 'treemap')
                                                                ? (_openBlock(), _createBlock(SpendingTreemapPage, {
                                                                    key: 1,
                                                                    embedded: true,
                                                                    tileBorderColour: colourMode.value === 'dark' ? '#404040' : undefined,
                                                                    filterCategory: selectedCategoryName.value,
                                                                    filterSubCategory: _unref(selectedSubCategory) || undefined
                                                                }, null, 8, ["tileBorderColour", "filterCategory", "filterSubCategory"]))
                                                                : (item.i === 'categories')
                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                                        _createElementVNode("h3", _hoisted_8, _toDisplayString(_unref(selectedSubCategory) ? 'Merchants' : 'Categories'), 1),
                                                                        _createElementVNode("div", _hoisted_9, [
                                                                            (_openBlock(), _createBlock(CategoryAnalysisList, {
                                                                                key: _unref(selectedSubCategory) || _unref(selectedFilter) || 'all',
                                                                                categories: currentCategories.value,
                                                                                merchants: currentMerchants.value,
                                                                                showMerchants: !_unref(selectedMerchant),
                                                                                initialTab: _unref(selectedSubCategory) ? 'merchants' : 'analysis',
                                                                                limit: 0,
                                                                                dateRange: rangeLabel.value,
                                                                                onCategoryClick: onSubCategoryClick,
                                                                                onMerchantClick: onMerchantClick
                                                                            }, null, 8, ["categories", "merchants", "showMerchants", "initialTab", "dateRange"]))
                                                                        ])
                                                                    ], 64))
                                                                    : (item.i === 'transactions')
                                                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                                                            _cache[12] || (_cache[12] = _createElementVNode("h3", { class: "chart-title" }, "Transactions", -1)),
                                                                            _createElementVNode("div", _hoisted_10, [
                                                                                _createVNode(TransactionsList, {
                                                                                    search: true,
                                                                                    showDateTotal: false,
                                                                                    transactions: apiTransactionGroups.value,
                                                                                    formatter: formatter.value,
                                                                                    today: _unref(state).today,
                                                                                    yesterday: _unref(state).yesterday,
                                                                                    dividingLines: true,
                                                                                    updatingTransactionId: updatingTransactionId.value,
                                                                                    onTransactionClick: goToTransaction,
                                                                                    categoryPills: true
                                                                                }, null, 8, ["transactions", "formatter", "today", "yesterday", "updatingTransactionId"])
                                                                            ])
                                                                        ], 64))
                                                                        : (item.i === 'recurring')
                                                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                                                                _cache[13] || (_cache[13] = _createElementVNode("h3", { class: "chart-title" }, "Recurring payments", -1)),
                                                                                _createElementVNode("div", _hoisted_11, [
                                                                                    _createVNode(RecurringPaymentsPage, {
                                                                                        embedded: true,
                                                                                        onTransactionClick: goToTransaction
                                                                                    })
                                                                                ])
                                                                            ], 64))
                                                                            : (item.i === 'merchants')
                                                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                                                                    _cache[14] || (_cache[14] = _createElementVNode("h3", { class: "chart-title" }, "Top merchants", -1)),
                                                                                    _createVNode(TopMerchantsChart, {
                                                                                        merchants: topMerchants.value,
                                                                                        formatter: formatter.value,
                                                                                        colourMode: colourMode.value,
                                                                                        onMerchantClick: onMerchantClick
                                                                                    }, null, 8, ["merchants", "formatter", "colourMode"])
                                                                                ], 64))
                                                                                : (item.i === 'tags')
                                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
                                                                                        _cache[15] || (_cache[15] = _createElementVNode("h3", { class: "chart-title" }, "Tagged spending", -1)),
                                                                                        _createElementVNode("div", _hoisted_12, [
                                                                                            _createVNode(SpendByCustomerTagPage)
                                                                                        ])
                                                                                    ], 64))
                                                                                    : _createCommentVNode("", true)
                                                    ], 2)
                                                ]),
                                                _: 2
                                            }, 1032, ["x", "y", "w", "h", "i"]));
                                        }), 128))
                                    ]),
                                    _: 1
                                }, 8, ["layout"])
                            ], 64))
                    ], 64)),
                _createVNode(DateRangeDialog, {
                    modelValue: dateDialogOpen.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((dateDialogOpen).value = $event)),
                    initialSelection: _unref(dateSelection),
                    onSelect: onDateRangeSelect
                }, null, 8, ["modelValue", "initialSelection"]),
                _createVNode(TransactionViewDialog, {
                    ref_key: "txnViewDialog",
                    ref: txnViewDialog,
                    transaction: selectedTxn.value,
                    onChangeCategory: onChangeCategoryFromView
                }, null, 8, ["transaction"]),
                _createVNode(ChangeCategoryDialog, {
                    ref_key: "singleTxnDialog",
                    ref: singleTxnDialog,
                    merchantName: selectedTxn.value?.merchantName,
                    currentClassification: selectedTxn.value?.effectiveClassification,
                    currentExcludeFromAnalysis: selectedTxn.value?.excludeFromAnalysis ?? false,
                    onApply: onSingleCategoryApply
                }, null, 8, ["merchantName", "currentClassification", "currentExcludeFromAnalysis"])
            ], 2));
        };
    }
});
